import NavigationTabs from "../../utils/components/NavigationTabs";
import Button from "../../utils/components/Button";
import Breadcrumbs from "../../utils/components/Breadcrumbs";
import SelectBox from "../../utils/components/SelectBox";
import { matchPath, Outlet, useLocation, useNavigate, useParams } from "react-router";
import { useCallback, useContext, useEffect, useState } from "react";
import { auth } from "../../App/routes";
import FormFieldsList from "./TemplateStructureComponents/FormFieldsList";
import { useProject } from "../../utils/hooks/useProject";
import API from "../../utils/API";
import { uniqueId } from "lodash";
import Field from "./components/TemplateStructureDetail/Field";
import { confirmAlert } from "react-confirm-alert";
import AlertModal from "../../utils/components/AlertModal";
import { formFieldInputs } from "../../utils/db/formFields";
import Modal from "../../utils/components/Modal";
import Input from "../../utils/components/Input";
import timeStampToDateFormat from "../../utils/helpers/timestampToDateFormat";
import { useCan } from "../../utils/hooks/useCan";
import { RequestContext } from "../../utils/context/RequestContext";
import { TEXTTYPE } from "../../utils/db/constants";

// Builds the page's editable template-structure shape (formTemplateStructure /
// editedFormTemplateStructure) from a raw `sub_forms` array as returned by
// GET /form_template/{id}. Extracted so it can be reused both on initial load
// and when refreshing after a cascading-set Excel import (see
// refreshFormTemplateStructureAfterImport below) without duplicating the mapping logic.
const buildFormTemplateStructure = (subForms) => {
  return (subForms || []).map((sub_form, formIndex) => {
    const fields = sub_form.controls.map((control, fieldIndex) => {
      if (control.field_type === "repeatableGroup") {
        const repeatedFields = control.controls.map((r_control, index) => {
          const rField = {
            ...r_control,
            fieldIndex: index,
            fieldType: r_control.field_type,
            formIndex: formIndex,
            repeatedFieldIndex: fieldIndex,
            order: index,
            source: "",
            id: uniqueId(`repeatable-${index}-`),
          };

          if (r_control.field_type === "staticImage") {
            rField.image = r_control.image_obj;
          }

          if (r_control.field_type === "staticDocument") {
            rField.file = r_control.file_obj
              ? { ...r_control.file_obj, name: r_control.file_obj.file_path?.split("/").pop() }
              : null;
          }

          if (r_control.field_type === "text") {
            rField.type =
              r_control.type === TEXTTYPE.EDITOR ? true : false;
          }

          return rField;
        });

        return {
          ...control,
          repeatedFields,
          isRepeatable: true,
          fieldType: "repeatable",
          id: uniqueId("repeatable-"),
        };
      } else {
        const field = {
          ...control,
          fieldIndex: fieldIndex,
          fieldType: control.field_type,
          formIndex: formIndex,
          order: fieldIndex,
          source: "",
          id: uniqueId(`field-${fieldIndex}-`),
        };

        if (control.field_type === "staticImage") {
          field.image = control.image_obj;
        }

        if (control.field_type === "staticDocument") {
          field.file = control.file_obj
            ? { ...control.file_obj, name: control.file_obj.file_path?.split("/").pop() }
            : null;
        }

        if (control.field_type === "text") {
          field.type = control.type === TEXTTYPE.EDITOR ? true : false;
        }

        return field;
      }
    });

    return {
      ...sub_form,
      fields,
      role: {
        id: sub_form.role_id,
        value: String(sub_form.role_id),
        label: sub_form.role_name,
      },
      order: formIndex,
    };
  });
};

const FormTemplatesDetails = ({ translate }) => {
  const { pathname } = useLocation();
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [templateInfo, setTemplateInfo] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedRevision, setSelectedRevision] = useState([]);
  const [isFormSidebarOpen, setIsFormSidebarOpen] = useState(false);

  const [newSubFormName, setNewSubFormName] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formTemplateStructure, setFormTemplateStructure] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [isNewCascadingSettingsPage, setIsNewCascadingSettingsPage] = useState(false);
  const [editedFormTemplateStructure, setEditedFormTemplateStructure] =
    useState([]);
  const [formDetails, setFormDetails] = useState({
    id_number: "",
    name: "",
    prefix: "",
    project_id: undefined,
    revision_set: undefined,
    site_set_level: undefined,
    site_set: undefined,
    status: undefined,
    status_set: undefined,
    counter_format: "",
    quick_start: undefined,
  });

  const [editedFormDetails, setEditedFormDetails] = useState(null);

  const [formHeaders, setFormHeaders] = useState([]);
  const [editedFormHeaders, setEditedFormHeaders] = useState([]);
  const [formAuthorization, setFormAuthorization] = useState([]);
  const [editedFormAuthorization, setEditedFormAuthorization] = useState([]);
  const [groups, setGroups] = useState([]);

  const [locationSets, setLocationSets] = useState([]);
  const [revisionSets, setRevisionSets] = useState([]);
  const [statusSets, setStatusSets] = useState([]);
  const [roles, setRoles] = useState([]);
  const [refresh, setRefresh] = useState(false);

  const [versions, setVersions] = useState([]);
  const [isLatestVersion, setIsLatestVersion] = useState(false);

  const { id } = useParams();
  const location = useLocation();
  const { project } = useProject();
  const navigate = useNavigate();

  const can = useCan();

  const { setRequestLoading } = useContext(RequestContext);

  useEffect(() => {
    if (
      !id &&
      location.pathname.includes(auth.newFormTemplate.link) &&
      !isEditing
    ) {
      setEditedFormDetails(JSON.parse(JSON.stringify(formDetails)));
      setRefresh(true);
      setIsEditing(true);
    } else if (id && String(formDetails?.id) !== String(id) && id !== "new") {
      setFormAuthorization([]);
      API.getData(`/form_template/${id}`).then(({ data: res }) => {
        const selected_reviston_set = res.data.revision_sets.find(
          (rev) => rev.selected,
        );
        // Revizyon seti silinmişse selected_reviston_set undefined olabilir.
        // Bu durumda revision_set null bırakılır; form bilgileri yine de yüklenir
        // ve kullanıcı yeni bir revizyon seti seçerek şablonu kaydedebilir.
        const revision_set = selected_reviston_set
          ? {
              id: selected_reviston_set.id,
              value: String(selected_reviston_set.id),
              label: selected_reviston_set.name,
            }
          : null;

        const selected_site_set_level = res.data.site_levels.find(
          (site) => site.selected,
        );

        let site_set_level = null;
        if (selected_site_set_level) {
          site_set_level = {
            id: selected_site_set_level.level,
            value: String(selected_site_set_level.level),
            label: `${translate("level")} ${selected_site_set_level.level}`,
          };
        }

        const selected_site_set = res.data.site_sets.find(
          (site) => site.selected,
        );

        let site_set = null;
        if (selected_site_set) {
          site_set = {
            id: selected_site_set.id,
            value: String(selected_site_set.id),
            label: selected_site_set.name,
            level: selected_site_set.level,
          };
        }

        const selected_status_set = res.data.status_sets.find(
          (status) => status.selected,
        );

        let status_set = null;
        if (selected_status_set) {
          status_set = {
            id: selected_status_set.id,
            value: String(selected_status_set.id),
            label: selected_status_set.name,
          };
        }

        let formAuthorization = [];

        if (res.data.authorizations) {
          formAuthorization = res.data.authorizations.map((auth) => ({
            group_id: auth.group_id || auth.id || undefined,
            is_access: auth.is_access || false,
            is_create: auth.is_create || false,
            is_view_all: auth.is_view_all || false,
            is_view_received: auth.is_view_received || false,
          }));

          setFormAuthorization(formAuthorization);
          setEditedFormAuthorization(formAuthorization);
        }

        const newFormDetails = {
          id: res.data.id,
          created_at: res.data.created_at,
          created_by: res.data.created_by,
          modified_at: res.data.modified_at,
          modified_by: res.data.modified_by,
          name: res.data.name,
          id_number: res.data.id_number,
          project_id: res.data.project_id,
          revision_set,
          site_set_level,
          site_set,
          status: {
            value: res.data.status.toLowerCase(),
            label: res.data.status.toLowerCase(),
          },
          quick_start: res.data.quick_start
            ? {
              value: res.data.quick_start,
              label: res.data.quick_start
                ? translate("yes")
                : translate("no"),
            }
            : {
              value: false,
              label: translate("no"),
            },
          status_set,
          version: `#${res.data.version}`,
          counter_format: res.data.counter_format,
          prefix: res.data.prefix,
          headers: res.data.headers,
        };

        setFormDetails({
          ...formDetails,
          ...newFormDetails,
        });

        setEditedFormDetails(
          JSON.parse(
            JSON.stringify({
              ...formDetails,
              ...newFormDetails,
            }),
          ),
        );

        API.getData(
          `/form_templates/versions/${res.data.original_id || res.data.id}`,
        ).then(({ data: versionRes }) => {
          setVersions(
            versionRes.data.map((version) => ({
              id: version.id,
              value: version.version,
              status: version.status,
              modified_at: version.modified_at,
              latest: version.latest,
            })),
          );
        });

        const formTemplateStructure = buildFormTemplateStructure(
          res.data.sub_forms,
        );

        setFormTemplateStructure(formTemplateStructure);
        setEditedFormTemplateStructure(
          JSON.parse(JSON.stringify(formTemplateStructure)),
        );

        setTimeout(() => {
          setRefresh(true);
        }, 50);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (
      ((isEditing &&
          project?.id &&
          !id &&
          location.pathname.includes(auth.newFormTemplate.link)) ||
        (id && String(formDetails?.id) === String(id) && !isEditing)) &&
      refresh
    ) {
      API.getData(`metadata_sets?project_id=${project.id}`).then(
        ({ data: res }) => {
          const newFormHeaders = res.data.map((item) => {
            const selectedHeaders = formDetails?.headers
              ?.find((header) => header.id === item.id)
              ?.data?.filter((detail) => detail.selected)
              .map((detail) => detail.id);

            return {
              metadata_set_id: item.id,
              metadata_set_name: item.name,
              multi_select: item.multi_select,
              details: item.details.map((detail) => ({
                id: detail.id,
                value: detail.id,
                label: detail.name,
                isSelected: selectedHeaders?.includes(detail.id),
              })),
            };
          });

          setFormHeaders([...newFormHeaders]);
          setEditedFormHeaders(JSON.parse(JSON.stringify(newFormHeaders)));
        },
      );

      API.getData(`groups?project_id=${project.id}&size=99999`).then(
        ({ data: res }) => {
          const newGroups = res.data
            ?.filter((item) => item.name)
            ?.map((item) => ({
              id: item.id,
              name: item.name,
              company_name: item.company_name,
            }));

          setGroups(newGroups);

          // Preserve existing authorization settings when editing
          const updatedAuthorization = newGroups.map((group) => {
            // Check if this group already has authorization settings
            const existingAuth = formAuthorization.find(
              (auth) => auth.group_id === group.id
            );

            // If found, use existing settings; otherwise, use defaults
            return existingAuth || {
              group_id: group.id,
              is_access: false,
              is_create: false,
              is_view_all: false,
              is_view_received: false,
            };
          });

          setEditedFormAuthorization(updatedAuthorization);
        },
      );

      if (locationSets.length === 0) {
        API.getData(`site_sets?project_id=${project.id}`).then(
          ({ data: res }) => {
            const locationSets = res.data.map((item) => ({
              id: item.id,
              value: String(item.id),
              label: item.name,
              level: item.level,
            }));

            setLocationSets(locationSets);
          },
        );
      }

      if (revisionSets.length === 0) {
        API.getData(`revision_sets?project_id=${project.id}`).then(
          ({ data: res }) => {
            const revisionSets = res.data.map((item) => ({
              id: item.id,
              value: String(item.id),
              label: item.name,
            }));

            setRevisionSets(revisionSets);
          },
        );
      }

      if (statusSets.length === 0) {
        API.getData(`status_sets?project_id=${project.id}`).then(
          ({ data: res }) => {
            const statusSets = res.data.map((item) => ({
              id: item.id,
              value: String(item.id),
              label: item.name,
            }));

            setStatusSets(statusSets);
          },
        );
      }

      if (roles.length === 0) {
        API.getData(`roles?project_id=${project.id}`).then(({ data: res }) => {
          const roles = res.data.map((item) => ({
            id: item.id,
            value: String(item.id),
            label: item.name,
          }));

          setRoles(roles);
        });
      }

      setRefresh(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditing, refresh]);

  useEffect(() => {
    if (id) {
      setTemplateInfo({
        id,
        name: formDetails.name,
      });
    }
  }, [formDetails.name, id]);

  useEffect(() => {
    if (!isEditing) {
      setSelectedField(null);
      setEditedFormAuthorization(formAuthorization);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  useEffect(() => {
    const templateStructureRoute = auth.formTemplateDetails.pages.find(
      (page) => page.id === "auth-form-template-details-template-structure",
    );

    if (templateStructureRoute) {
      const match = matchPath(
        {
          path:
            auth.formTemplateDetails.link + `/${templateStructureRoute.link}`,
          end: true,
          caseSensitive: true,
        },
        location?.pathname,
      );

      if (match) setIsFormSidebarOpen(true);
      else setIsFormSidebarOpen(false);
    }
  }, [location]);

  useEffect(() => {
    if (versions.length) {
      const latestVersion = versions.find((version) => version.latest);
      const currentVersion = versions.find(
        (version) => String(version.id) === String(id),
      );

      if (currentVersion)
        setSelectedRevision({
          value: currentVersion.id,
          label: `#${currentVersion.value} - ${timeStampToDateFormat(
            currentVersion.modified_at,
          )}`,
        });

      if (latestVersion && currentVersion) {
        if (String(latestVersion.id) === String(currentVersion.id))
          setIsLatestVersion(true);
        else setIsLatestVersion(false);
      }
    }
  }, [id, versions]);

  const handleUpdateField = (e) => {
    if (!selectedField) return;

    if (
      !e?.target?.name ||
      !e?.target?.type ||
      (!e?.target?.value && !String(e?.target?.checked))
    )
      return;

    const newField = { ...selectedField };

    if (e.target?.dataset?.action?.includes("upload")) {
      const [, type] = e.target.dataset.action.split(":");

      if (!type) return;

      const formData = new FormData();

      formData.append("File", e.target.files[0]);

      if (type === "reference_data_source") {
        API.postFile("excel/columns", formData).then(({ data: res }) => {
          console.log(res.data, newField);
        });
      }
    }

    if (e.target.type === "checkbox") {
      newField[e.target.name] = e.target.checked;
    } else if (e.target.type === "file" && e.target.files) {
      const file = e.target.files[0];

      const reader = new FileReader();
      reader.onload = (event) => {
        newField[e.target.name] = {
          base64: event.target.result,
          name: file.name,
        };

        setSelectedField(newField);

        handleUpdateFormTemplateStructure(newField);
      };
      reader.readAsDataURL(file);
      return;
    } else {
      newField[e.target.name] = e.target.value;
    }

    setSelectedField(newField);

    handleUpdateFormTemplateStructure(newField);
  };

  const handleAddNewSubForm = (e) => {
    e.preventDefault();

    if (!newSubFormName) return;

    setEditedFormTemplateStructure([
      ...editedFormTemplateStructure,
      {
        id: uniqueId("subform-"),
        name: newSubFormName,
        role: null,
        fields: [],
        index: editedFormTemplateStructure.length,
      },
    ]);

    setNewSubFormName("");
    setIsModalOpen(false);
  };

  const handleUpdateFormTemplateStructure = (newField) => {
    const newEditedFormTemplateStructure = [...editedFormTemplateStructure];
    const newEditedFields = [
      ...newEditedFormTemplateStructure[newField.formIndex].fields,
    ];

    if (newField.repeatedFieldIndex !== null)
      newEditedFields[newField.fieldIndex].repeatedFields[
        newField.repeatedFieldIndex
        ] = newField;
    else newEditedFields[newField.fieldIndex] = newField;

    newEditedFormTemplateStructure[newField.formIndex].fields = newEditedFields;

    setEditedFormTemplateStructure(newEditedFormTemplateStructure);
  };

  const isCascadingSet = useCallback((field) => {
    return (
      field?.fieldType === "selection" &&
      field?.data_source === "from_cascading_set"
    );
  }, []);

  // Cascading Excel import (CascadingLists.js handleAddDetail /
  // CascadingListItem/index.js handleUploadExcel) persists the cascading wiring
  // (cascading_main_field_code/cascading_sub_field_code/cascading_values) directly to the
  // DB row for the currently-open template, independently of this page's in-memory state.
  // Without this refresh, editedFormTemplateStructure stays a stale, page-load-time
  // snapshot, and pressing the Save button (still visible on the Cascading Lists tab for
  // existing templates) would submit that stale snapshot and wipe the just-imported
  // cascading chain back out on the backend. Re-fetching here keeps the Save payload in
  // sync with what the import actually persisted.
  const refreshFormTemplateStructureAfterImport = useCallback(() => {
    if (!id || id === "new") return Promise.resolve();

    return API.getData(`/form_template/${id}`).then(({ data: res }) => {
      const refreshedStructure = buildFormTemplateStructure(res.data.sub_forms);

      setFormTemplateStructure(refreshedStructure);
      setEditedFormTemplateStructure(
        JSON.parse(JSON.stringify(refreshedStructure)),
      );
    });
  }, [id]);

  const handleSubmitNewTemplate = (e) => {
    e.preventDefault();

    let roleError = false;
    const subFormsWithoutRole = [];

    const subFormsErrors = editedFormTemplateStructure.some((item, i) => {
      if (!item.role) {
        subFormsWithoutRole.push(item.name);
        roleError = true;
        return true;
      }

      let controlError = item.fields.some((field) => {
        if (field.isRepeatable) {
          return field.repeatedFields.some((control) => {
            if (isCascadingSet(control)) return false;
            return "values" in control && control.values.length < 1;
          });
        }

        if (isCascadingSet(field)) return false;
        return "values" in field && field.values.length < 1;
      });

      return controlError;
    });

    if (roleError) {
      confirmAlert({
        customUI: ({ onClose }) => (
          <AlertModal
            buttons={[
              {
                label: translate("okay"),
                onClick: onClose,
                classNames: "btn btn-primary",
              },
            ]}
            message={translate(
              "form_templates.template_structure.company_role_error",
              {
                subFormName: subFormsWithoutRole[0],
              },
            )}
          />
        ),
      });
      return false;
    }

    if (subFormsErrors) {
      confirmAlert({
        customUI: ({ onClose }) => (
          <AlertModal
            buttons={[
              {
                label: translate("okay"),
                onClick: onClose,
                classNames: "btn btn-primary",
              },
            ]}
            message={translate("fill_all_fields")}
          />
        ),
      });

      return false;
    }

    const {
      id_number,
      name,
      prefix,
      revision_set,
      site_set_level,
      site_set,
      status,
      status_set,
      counter_format,
      quick_start,
    } = editedFormDetails;

    if (
      id_number === "" ||
      name === "" ||
      prefix === "" ||
      !revision_set?.id ||
      !site_set?.id ||
      !status?.value ||
      !status_set?.id ||
      !site_set_level?.value ||
      counter_format === ""
    ) {
      confirmAlert({
        customUI: ({ onClose }) => (
          <AlertModal
            buttons={[
              {
                label: translate("okay"),
                onClick: onClose,
                classNames: "btn btn-secondary",
              },
            ]}
            message={translate("fill_all_fields")}
          />
        ),
      });
      return;
    }

    const authorizations = editedFormAuthorization;

    const headers = editedFormHeaders
      .filter(
        (header) =>
          header.details.filter((detail) => detail.isSelected).length > 0,
      )
      .map((item) => ({
        metadata_set_id: item.metadata_set_id,
        details: item.details
          .filter((detail) => detail.isSelected)
          .map((detail) => detail.id),
      }));

    const payloadSubForms = [...editedFormTemplateStructure];

    const sub_forms = payloadSubForms.map((item, i) => {
      let controls = item.fields.map((field) => {
        const fieldPayload = { ...field };
        if (fieldPayload.isRepeatable) {
          const payloadControls = [];

          fieldPayload.repeatedFields.forEach((repeatedField) => {
            const payloadControl = {};

            const selectedField = formFieldInputs[repeatedField.fieldType];
            selectedField.forEach((fieldItem) => {
              let fieldValue = repeatedField[fieldItem.name];

              const { type: fieldType } = fieldItem;

              if (fieldValue) {
                if (fieldType === "number") {
                  fieldValue = fieldValue ? Number(fieldValue) : fieldValue;
                } else if (fieldType === "image") {
                  fieldValue = fieldValue?.base64 || fieldValue || null;
                } else if (fieldType === "file") {
                  fieldValue = fieldValue?.base64 || fieldValue || null;
                }
              } else if (!fieldValue) {
                if ("defaultValue" in fieldItem)
                  fieldValue =
                    fieldItem.defaultValue !== "" ||
                    fieldItem.defaultValue !== undefined
                      ? fieldItem.defaultValue
                      : null;
                else if ("defaultOption" in fieldItem)
                  fieldValue =
                    fieldItem.defaultOption !== "" ||
                    fieldItem.defaultOption !== undefined
                      ? fieldItem.defaultOption
                      : null;
                else if ("defaultValues" in fieldItem)
                  fieldValue = fieldItem.defaultValues || null;
                else fieldValue = null;
              }

              if (
                fieldItem.name === "type" &&
                repeatedField.fieldType === "text"
              ) {
                fieldValue = TEXTTYPE[fieldValue ? "EDITOR" : "INPUT"];
              }

              if (fieldType === "image" && typeof fieldValue === "object") {
                payloadControl[fieldItem.name] = "";
                payloadControl["image_obj"] = fieldValue;
              } else if (fieldType === "file" && fieldValue && typeof fieldValue === "object") {
                payloadControl[fieldItem.name] = "";
                const { name, ...fileObjForPayload } = fieldValue;
                payloadControl["file_obj"] = fileObjForPayload;
              } else payloadControl[fieldItem.name] = fieldValue;
              payloadControl["field_type"] = repeatedField.fieldType;
            });

            if (isCascadingSet(repeatedField)) {
              payloadControl["cascading_main_field_code"] = repeatedField.cascading_main_field_code || null;
              payloadControl["cascading_sub_field_code"] = repeatedField.cascading_sub_field_code || null;
              payloadControl["cascading_values"] = repeatedField.cascading_values || null;
            }

            payloadControls.push(payloadControl);
          });

          return {
            field_code: uniqueId("RptG"),
            field_type: "repeatableGroup",
            controls: payloadControls,
          };
        }

        const payloadControl = {};

        const selectedField = formFieldInputs[fieldPayload.fieldType];
        selectedField.forEach((fieldItem) => {
          let fieldValue = fieldPayload[fieldItem.name];

          const { type: fieldType } = fieldItem;

          if (String(fieldValue).length > 0) {
            if (fieldType === "number") {
              fieldValue = fieldValue ? Number(fieldValue) : fieldValue;
            } else if (fieldType === "image") {
              fieldValue = fieldValue?.base64 || fieldValue || null;
            } else if (fieldType === "file") {
              fieldValue = fieldValue?.base64 || fieldValue || null;
            } else
              fieldValue = fieldItem?.allowZero
                ? fieldValue
                : fieldValue || "" || null;
          } else if (!fieldValue) {
            if ("defaultValue" in fieldItem)
              fieldValue =
                fieldItem.defaultValue !== "" ||
                fieldItem.defaultValue !== undefined
                  ? fieldItem.defaultValue
                  : null;
            else if ("defaultOption" in fieldItem)
              fieldValue =
                fieldItem.defaultOption !== "" ||
                fieldItem.defaultOption !== undefined
                  ? fieldItem.defaultOption
                  : null;
            else if ("defaultValues" in fieldItem)
              fieldValue = fieldItem.fieldValues || null;
            else fieldValue = "" || null;
          }

          if (fieldItem.name === "url") {
            fieldValue = !fieldValue.startsWith("http")
              ? `https://${fieldValue}`
              : fieldValue;
          }

          if (fieldItem.name === "type" && fieldPayload.fieldType === "text") {
            fieldValue = TEXTTYPE[fieldValue ? "EDITOR" : "INPUT"];
          }

          if (fieldType === "image" && typeof fieldValue === "object") {
            payloadControl[fieldItem.name] = "";
            payloadControl["image_obj"] = fieldValue;
          } else if (fieldType === "file" && fieldValue && typeof fieldValue === "object") {
            payloadControl[fieldItem.name] = "";
            const { name, ...fileObjForPayload } = fieldValue;
            payloadControl["file_obj"] = fileObjForPayload;
          } else payloadControl[fieldItem.name] = fieldValue;
          payloadControl["field_type"] = fieldPayload.fieldType;
        });

        if (isCascadingSet(fieldPayload)) {
          payloadControl["cascading_main_field_code"] = fieldPayload.cascading_main_field_code || null;
          payloadControl["cascading_sub_field_code"] = fieldPayload.cascading_sub_field_code || null;
          payloadControl["cascading_values"] = fieldPayload.cascading_values || null;
        }

        return payloadControl;
      });

      return {
        index: i + 1,
        name: item.name,
        role_id: item.role.id,
        controls,
      };
    });

    const data = {
      authorizations,
      headers,
      sub_forms,
      id_number,
      name,
      prefix,
      project_id: project.id,
      revision_set_id: revision_set.id,
      site_level: parseInt(site_set_level.value),
      site_set_id: site_set.id,
      status: status.value,
      status_set_id: status_set.id,
      counter_format,
      quick_start: quick_start?.value,
    };

    setRequestLoading(true);

    if (!id && location.pathname.includes(auth.newFormTemplate.link)) {
      API.postData(`form_templates?project_id=${project.id}`, data).then(
        ({ data: res }) => {
          navigate(auth.formTemplateDetails.link.replace(":id", res.data.id));
          setIsEditing(false);
        },
      );
    }
    else {
      API.putData(`form_template/${id}`, data).then(({ data: res }) => {
        navigate(auth.formTemplateDetails.link.replace(":id", res.data.id));
        setIsEditing(false);
      });
    }
  };

  useEffect(() => {
    const cascadingPage = auth.formTemplateDetails.pages.find(p => p.id === "auth-form-template-details-cascading-lists");
    const isNewPageCascadingSettings = pathname.includes(cascadingPage.link);

    setIsNewCascadingSettingsPage(isNewPageCascadingSettings && id === "new");
  }, [pathname]);

  return (
    <div className="FormTemplateDetails">
      <div className="container-fluid py-2">
        <div className="row mb-4">
          <div className="col-12 col-lg-8">
            <Breadcrumbs data={breadcrumbs}/>
          </div>
          {id && (
            <div className="col-12 col-lg-4 d-flex align-items-center justify-content-end">
              <SelectBox
                options={versions.map((item) => ({
                  value: item.id,
                  label: `#${item.value} - ${timeStampToDateFormat(
                    item.modified_at,
                  )}`,
                }))}
                placeholder="Select a revision"
                onChange={(selectedRevision) =>
                  navigate(
                    auth.formTemplateDetails.link.replace(
                      ":id",
                      selectedRevision.value,
                    ),
                  )
                }
                value={selectedRevision}
              />
              {!isEditing &&
                isLatestVersion &&
                can.doThat("formTemplate:edit") && (
                  <Button
                    className="btn-primary px-4 py-2 ms-2"
                    onClick={() => setIsEditing(true)}
                  >
                    {translate("edit")}
                  </Button>
                )}
            </div>
          )}
        </div>
        <div className="row">
          {isEditing && isFormSidebarOpen && (
            <div className="col-12 col-lg-3">
              <div
                className="bg-white border rounded d-flex flex-column overflow-hidden position-sticky"
                style={{ top: "100px" }}
              >
                <div className="text-center text-uppercase py-3 border-bottom-orange">
                  {selectedField
                    ? translate(
                      "form_templates.add_edit_structure.field_details",
                    )
                    : translate(
                      "form_templates.add_edit_structure.form_fields",
                    )}
                </div>
                <div className="input-area px-3 pt-4 pb-3 d-flex flex-column justify-content-between overflow-auto">
                  {selectedField ? (
                    <Field
                      id={selectedField.order}
                      fieldType={selectedField.fieldType}
                      onChange={handleUpdateField}
                      values={selectedField}
                    />
                  ) : (
                    <FormFieldsList
                      translate={translate}
                      setEditedFormTemplateStructure={
                        setEditedFormTemplateStructure
                      }
                      editedFormTemplateStructure={editedFormTemplateStructure}
                      setIsModalOpen={setIsModalOpen}
                    />
                  )}
                </div>
                {selectedField ? (
                  <button
                    className="form-fields-button bg-transparent border-bottom-orange text-uppercase text-center py-2"
                    onClick={() => setSelectedField(null)}
                  >
                    {translate("form_templates.add_edit_structure.form_fields")}
                  </button>
                ) : (
                  ``
                )}
              </div>
            </div>
          )}
          <div
            className={`col-12 ${
              isEditing && isFormSidebarOpen ? `col-lg-9` : ``
            }`}
          >
            <div className="bg-white border rounded-top pt-2 mt-4 mt-lg-0 h-100">
              <NavigationTabs
                tabs={auth.formTemplateDetails.pages
                  ?.map((item) => ({
                    id: item.id,
                    title: item.name,
                    link:
                      auth.formTemplateDetails.link.replace(
                        ":id",
                        id || "new",
                      ) + (item.link ? `/${item.link}` : ""),
                  }))}
                isUsingOutlet
              />
              <Outlet
                context={{
                  setBreadcrumbs,
                  templateInfo,
                  isEditing,
                  setIsEditing,
                  formTemplateStructure: isEditing
                    ? editedFormTemplateStructure
                    : formTemplateStructure,
                  setFormTemplateStructure: isEditing
                    ? setEditedFormTemplateStructure
                    : setFormTemplateStructure,
                  setSelectedField,
                  selectedField,
                  translate,
                  formDetails,
                  setFormDetails,
                  editedFormDetails,
                  setEditedFormDetails,
                  isNew:
                    !id &&
                    location.pathname.includes(auth.newFormTemplate.link),
                  formHeaders,
                  setFormHeaders,
                  editedFormHeaders,
                  setEditedFormHeaders,
                  formAuthorization,
                  setFormAuthorization,
                  editedFormAuthorization,
                  setEditedFormAuthorization,
                  groups,
                  locationSets,
                  revisionSets,
                  statusSets,
                  roles,
                  onCascadingImportSuccess: refreshFormTemplateStructureAfterImport,
                }}
              />
            </div>
          </div>
        </div>
        {isEditing && !isNewCascadingSettingsPage && (
          <div className="row px-3 mt-4 mb-3">
            <div className="col-12 bg-white shadow-sm border rounded d-flex flex-wrap justify-content-between py-4">
              <Button
                className="btn-secondary px-5"
                onClick={() => {
                  if (
                    !id &&
                    location.pathname.includes(auth.newFormTemplate.link)
                  )
                    navigate(auth.formTemplates.link);
                  else {
                    setIsEditing(false);
                    setEditedFormDetails(
                      JSON.parse(JSON.stringify(formDetails)),
                    );
                    setEditedFormHeaders(
                      JSON.parse(JSON.stringify(formHeaders)),
                    );
                    setEditedFormTemplateStructure(
                      JSON.parse(JSON.stringify(formTemplateStructure)),
                    );
                  }
                }}
              >
                {translate("cancel")}
              </Button>
              <Button
                type="submit"
                className="btn-blue px-5"
                onClick={handleSubmitNewTemplate}
              >
                {translate("save")}
              </Button>
            </div>
          </div>
        )}
      </div>

      <Modal
        title={translate("form_templates.template_structure.add_subform.title")}
        positiveButtonNode={translate("save")}
        negativeButtonNode={translate("cancel")}
        positiveButtonHandler={handleAddNewSubForm}
        modalSize="lg"
        show={isModalOpen}
        setShow={(show) => {
          setIsModalOpen(show);
        }}
      >
        <form onSubmit={handleAddNewSubForm}>
          <div className="form-group">
            <Input
              type="text"
              name="newSubFormName"
              title={translate(
                "form_templates.template_structure.add_subform.input_label",
              )}
              placeholder={translate(
                "form_templates.template_structure.add_subform.input_placeholder",
              )}
              value={newSubFormName}
              onChange={(e) => setNewSubFormName(e.target.value)}
            />
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default FormTemplatesDetails;
